import React, { useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  addEdge,
  Controls,
  useNodesState,
  useEdgesState,
  Connection,
  ReactFlowInstance,
  MiniMap,
  Background
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import { Tooltip } from '@mui/material';

const getLayoutedElements = (nodes: any, edges: any) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  const nodeWidth = 172;
  const nodeHeight = 36;

  dagreGraph.setGraph({ rankdir: 'TB' });

  nodes.forEach((node: any) => {
    dagreGraph.setNode(node.id, {
      width: nodeWidth,
      height: nodeHeight
    });
  });

  edges.forEach((edge: any) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node: any) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = 'top';
    node.sourcePosition = 'bottom';

    // We are shifting the dagre node position (anchor=center center) to the top left
    // so it matches the React Flow node anchor point (top left).
    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2
    };

    node.data.label = (
      <Tooltip title={node?.data?.label}>
        <div>
          {node?.data?.label.length > 18
            ? node?.data?.label?.slice(0, 18) + '...'
            : node?.data?.label}
        </div>
      </Tooltip>
    );

    // Add red background color
    node.style = {
      backgroundColor:
        node?.data?.mandatory || node?.data?.mandatory ? '#808080' : 'white',
      color:
        node?.data?.mandatory || node?.data?.mandatory ? 'white' : '#808080'
    };

    return node;
  });

  // edges.forEach((edge: any) => {
  //   edge.animated = true;
  // });

  edges.forEach((edge: any) => {
    // Only add edges that have valid source and target
    if (edge.source && edge.target) {
      edge.animated = true;
    } else {
      console.warn('Invalid edge detected:', edge);
    }
  });

  return { nodes, edges };
};

export interface FlowComponentsProps {
  onNodeClick?(element: any): void;
  height?: string;
  nodes: any[];
  edges: any[];
  width?: number | string;
  isOpen?: boolean;
}

const DependancyGraph: React.FC<FlowComponentsProps> = ({
  nodes: initialNodes,
  edges: initialEdges,
  height = '50vh',
  isOpen
}: any) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const reactFlowWrapper = useRef<HTMLDivElement | null>(null);
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (reactFlowInstance.current) {
        reactFlowInstance.current.fitView({
          padding: 0.2,
          duration: 200
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    const layoutedElements = getLayoutedElements(initialNodes, initialEdges);

    setNodes(layoutedElements.nodes);
    setEdges(layoutedElements.edges);

    const timer = setTimeout(() => {
      if (reactFlowInstance.current) {
        reactFlowInstance.current.fitView({
          padding: 0.2,
          duration: 200
        });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [initialNodes, initialEdges]);

  useEffect(() => {
    if (reactFlowInstance.current) {
      console.log();
      reactFlowInstance.current.fitView();
    }
  }, [nodes, edges, isOpen]);

  const onConnect = (params: Connection) => {
    // Add the edge to the state
    setEdges(eds => {
      const newEdges = addEdge(params, eds);

      // After adding the edge, trigger the view recalculation
      if (reactFlowInstance.current) {
        reactFlowInstance.current.fitView();
      }

      return newEdges;
    });
  };

  const handleNodeChange = (changes: any) => {
    onNodesChange(changes); // Updates the internal node state

    if (reactFlowInstance.current) {
      reactFlowInstance.current.fitView();
    }
  };

  const handleEdgeChange = (changes: any) => {
    onEdgesChange(changes); // Updates the internal edge state

    if (reactFlowInstance.current) {
      reactFlowInstance.current.fitView();
    }
  };

  return (
    <div
      style={{
        height: height,
        background: '#eeeeee',
        width: '100%'
      }}
      ref={reactFlowWrapper}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onConnect={onConnect}
        onInit={(instance: ReactFlowInstance) => {
          reactFlowInstance.current = instance;
          setTimeout(() => {
            instance.fitView({
              padding: 0.2,
              duration: 200
            });
          }, 50);
        }}
        onNodesChange={handleNodeChange}
        onEdgesChange={handleEdgeChange}
        fitView={true}
        fitViewOptions={{
          padding: 0.2,
          duration: 200
        }}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        style={{ color: 'black' }}
      >
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

export default DependancyGraph;
